import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { PhoneOutlined } from "@ant-design/icons";
import ErrorBanner    from "../../components/sharedcomponents/ErrorBanner";
import LoadingState   from "./components/listingsection/LoadingState";
import Pagination     from "../../components/sharedcomponents/Pagination";
import ListingCard    from "./components/listingsection/ListingCard";
import GBPPerformance from "../performance/GBPPerformance";
import ReviewsModal   from "../../components/sharedcomponents/Review/ReviewsModal";
import PhotosModal    from "./components/listingsection/listingFooter/PhotosModal";
import PostsModal     from "./components/listingsection/listingFooter/postsmodal/PostsModal";
import AddPhoto       from "./components/listingsection/listingFooter/AddPhoto";
 import ListingDetailsModal from "./components/listingsection/listingFooter/ListingDetailsModal";
import ListingToolbar, { SortOrder } from "./components/listingsfilter/ListingToolbar";
import { StatusFilterBar } from "./components/listingsfilter/StatusFilterBar";
import {
  useGBPListingManagement,
  useFilteredLocations,
  type LocationApi } from "./hooks/useGBPListingManagement";
import { useLocationFilters } from "./hooks/useLocationFilters";
import { useListingModals }   from "./hooks/useListingModals";
import { mapLocationToGBP }   from "./types/mapLocationToGBP";
 
 interface GBPManagementProps {
  onNavigateHome?: () => void;
  websiteId?: number;
  subjectEmail: string;
  onNavigateToWebsites?: () => void;
  gbpAccountId?: string;
}

 const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgo  = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10);
};

 const ListingManagement: React.FC<GBPManagementProps> = ({ websiteId, subjectEmail ,gbpAccountId}) => {

   const {
    gbp, gOnlyLocations, accountsLoading, accountsError,
    selectedAccount, normalizeHost,
    handleOpenReviews, handleOpenPerformance,
    selectedGbpAccountId, dateRange, setDateRange,
  } = useGBPListingManagement(websiteId, subjectEmail, gbpAccountId);

  const { filtered: filteredByStatus, counts, activeFilter, setActiveFilter } =
    useLocationFilters(gOnlyLocations);

   const [activePeriod, setActivePeriod] = useState<number | null>(30);
  const [gOnlySearch,  setGOnlySearch]  = useState("");
  const [sortOrder,    setSortOrder]    = useState<SortOrder>("none");
  const [goPage,       setGoPage]       = useState(1);
  const [goPerPage,    setGoPerPage]    = useState(6);

   const m = useListingModals();
  const { error, clearError } = gbp;

   const handleQuickRange = useCallback((days: number) => {
    setDateRange({ startDate: daysAgo(days - 1), endDate: todayStr() });
    setActivePeriod(days);
    setSortOrder("desc");
  }, [setDateRange]);

  const handleDateChange = (field: "startDate" | "endDate", value: string) => {
    setDateRange((p) => ({ ...p, [field]: value }));
    setActivePeriod(null);
    setSortOrder("none");
  };
   const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current && !dateRange.startDate && !dateRange.endDate) {
      initialized.current = true;
      handleQuickRange(30);
    }
  }, [dateRange.startDate, dateRange.endDate, handleQuickRange]);

   useEffect(() => {
    setGoPage(1);
  }, [gOnlySearch, selectedAccount, gOnlyLocations.length, sortOrder,
    dateRange.startDate, dateRange.endDate, activeFilter]);

   const sortedLocations = useMemo<LocationApi[]>(() => {
    if (sortOrder === "none") return filteredByStatus;
    return [...filteredByStatus].sort((a, b) => {
      const ca = typeof a.callClicks === "number" ? a.callClicks : -1;
      const cb = typeof b.callClicks === "number" ? b.callClicks : -1;
      return sortOrder === "desc" ? cb - ca : ca - cb;
    });
  }, [filteredByStatus, sortOrder]);

  const { total: goTotal, pageItems: goItems } = useFilteredLocations(
    sortedLocations, gOnlySearch, goPage, goPerPage,
  );

  const resultLabel = goTotal === 0 ? "Aucune fiche"
    : goTotal === 1 ? "1 fiche" : `${goTotal} fiches`;

   return (
    <div className="flex flex-col gap-0 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">

       <ListingToolbar
        search={gOnlySearch}
        onSearchChange={setGOnlySearch}
        dateRange={dateRange}
        onDateChange={handleDateChange}
        activePeriod={activePeriod}
        onQuickRange={handleQuickRange}
        sortOrder={sortOrder}
        onSortChange={setSortOrder}
      />

       <div className="px-5 py-3 border-b border-slate-50 bg-slate-50/50">
        <StatusFilterBar
          counts={counts}
          active={activeFilter}
          onChange={(f) => { setActiveFilter(f); setGoPage(1); }}
        />
      </div>

       {accountsError && (
        <div className="px-5 pt-4">
          <ErrorBanner message={accountsError} />
        </div>
      )}

       <div className="px-5 py-5">
        {accountsLoading ? (
          <LoadingState text="Chargement des fiches…" />
        ) : goItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-1">
              <PhoneOutlined className="text-slate-400" style={{ fontSize: 18 }} />
            </div>
            <p className="text-sm font-semibold text-slate-600">Aucune fiche trouvée</p>
            <p className="text-xs text-slate-400 max-w-xs">
              {activeFilter !== "all"
                ? "Aucune fiche ne correspond à ce filtre de statut."
                : "Modifiez vos critères de recherche ou changez la période."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {goItems.map((loc: LocationApi) => {
               const gbpObj = mapLocationToGBP(
                loc, normalizeHost, selectedAccount, selectedGbpAccountId,
              );
              return (
                <ListingCard
                  key={loc.name}
                  gbp={gbpObj}
                  days={activePeriod ?? undefined}
                  onDetails={m.openDetails}
                  onOpenReviews={(g) =>
                    handleOpenReviews(
                      g,
                      m.setReviewsModalOpen,
                      m.setReviewsLocationName,
                      m.setReviewsFicheId,
                    )
                  }
                  onPerformance={(g) =>
                    handleOpenPerformance(
                      g,
                      m.setPerfLocationName,
                      m.setPerfFicheName,
                    )
                  }
                  onPhotos={m.openPhotos}
                  onPosts={m.openPosts}
                />
              );
            })}
          </div>
        )}
      </div>

       {goTotal > 0 && (
        <div className="px-5 pb-5 border-t border-slate-50 pt-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] text-slate-400">
              {resultLabel}
              {activeFilter !== "all" && (
                <span className="ml-1 text-slate-300">· filtre actif</span>
              )}
            </span>
          </div>
          <Pagination
            total={goTotal}
            perPage={goPerPage}
            currentPage={goPage}
            onPageChange={setGoPage}
            onPerPageChange={setGoPerPage}
          />
        </div>
      )}

       {error && (
        <div className="px-5 pb-4">
          <ErrorBanner message={error} onClose={clearError} />
        </div>
      )}

    
      <ListingDetailsModal
        isOpen={m.detailsOpen}
        onClose={m.closeDetails}
        gbp={m.detailsGBP}
        subjectEmail={subjectEmail} 
      />
 <ReviewsModal
  isOpen={m.reviewsModalOpen}
  onClose={m.closeReviews}
  ficheId={m.reviewsFicheId}
  locationName={m.reviewsLocationName}
  subjectEmail={subjectEmail} 
 />
      <PhotosModal
        isOpen={m.photosModalOpen}
        onClose={m.closePhotos}
        gbp={m.selectedGBPForPhotos}
         subjectEmail={subjectEmail}
        onAdd={m.openAddPhoto}
      />
      <PostsModal
        isOpen={m.postsModalOpen}
        onClose={m.closePosts}
        gbp={m.selectedGBPForPosts}
        subjectEmail={subjectEmail}
        onAdd={m.openAddPost}
      />
      <AddPhoto isOpen={m.addPhotoOpen} onClose={m.closeAddPhoto} />
 
      {m.perfLocationName && (
        <GBPPerformance
          locationName={m.perfLocationName}
          ficheName={m.perfFicheName}
           subjectEmail={subjectEmail}
          onClose={m.closePerformance}
        />
      )}
    </div>
  );
};

export default ListingManagement;